import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import RelatedVideos from '@/pages/detail/components/RelatedVideos';
import { getVideoById, getCategory, formatPlays } from '@/lib/videos';
import { useFavorites } from '@/hooks/useFavorites';
import { usePageMeta } from '@/hooks/usePageMeta';

export default function Detail() {
  const { id = '' } = useParams();
  const video = getVideoById(id);
  const category = video ? getCategory(video.category) : undefined;
  const { isFavorite, toggleFavorite } = useFavorites();
  const [tip, setTip] = useState('');

  usePageMeta({
    title: video ? `${video.title} - 男同视频详情` : '视频详情 - 男同视频',
    description: video
      ? `${video.description}（${video.year}年 · ${video.duration}）`.slice(0, 150)
      : '男同视频详情页',
    keywords: video ? `${video.title},男同视频,${category?.name ?? ''}男同视频` : '男同视频',
  });

  if (!video || !category) {
    return (
      <div className="mx-auto flex w-full max-w-7xl flex-col items-center justify-center gap-4 px-4 py-24 text-center md:px-8">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-file-unknow-line text-3xl" />
        </span>
        <h1 className="text-xl font-bold text-foreground-950">内容不存在或已下架</h1>
        <p className="text-sm text-foreground-500">该视频可能已被移除，去看看其他精彩内容吧。</p>
        <Link
          to="/"
          className="flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50"
        >
          <i className="ri-home-5-line" />
          返回首页
        </Link>
      </div>
    );
  }

  const favorited = isFavorite(video.id);

  const handlePlay = () => {
    setTip('播放功能为后续接入，当前以静态封面演示。');
    window.setTimeout(() => setTip(''), 2600);
  };

  const handleFavorite = () => {
    const next = toggleFavorite(video.id);
    setTip(next ? '已加入收藏，可在本地随时查看。' : '已取消收藏。');
    window.setTimeout(() => setTip(''), 2200);
  };

  const infoRows = [
    { label: '制作年份', value: `${video.year} 年`, icon: 'ri-calendar-line' },
    { label: '视频类型', value: category.name, icon: 'ri-movie-line' },
    { label: '视频时长', value: video.duration, icon: 'ri-time-line' },
    { label: '上传日期', value: video.uploadDate, icon: 'ri-upload-cloud-line' },
    { label: '播放热度', value: `${formatPlays(video.plays)}次`, icon: 'ri-fire-line' },
    { label: '内容评分', value: video.rating.toFixed(1), icon: 'ri-star-line' },
  ];

  return (
    <>
      <article className="mx-auto w-full max-w-7xl px-4 py-6 md:px-8 md:py-10">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: category.name, to: `/category/${category.key}` },
            { label: video.title },
          ]}
        />

        <div className="mt-4 overflow-hidden rounded-lg border border-background-200/80 bg-background-50">
          <div className="relative aspect-video w-full">
            <img
              src={video.cover}
              alt={video.title}
              title={`${video.title} - 男同视频`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-primary-950/70 via-primary-950/10 to-transparent" />
            <button
              type="button"
              onClick={handlePlay}
              aria-label="播放视频（演示）"
              className="absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-accent-500/95 text-background-50 transition-transform hover:scale-105 md:h-20 md:w-20"
            >
              <i className="ri-play-fill text-3xl md:text-4xl" />
            </button>
            <span className="absolute left-3 top-3 flex items-center gap-1 rounded-md bg-primary-950/60 px-2.5 py-1 text-xs font-medium text-background-50 backdrop-blur-sm">
              <i className={`${category.icon} text-accent-300`} />
              {category.name}
            </span>
            <span className="absolute bottom-3 right-3 flex items-center gap-1 rounded-md bg-primary-950/70 px-2.5 py-1 text-xs font-semibold text-background-50">
              <i className="ri-time-line text-accent-300" />
              {video.duration}
            </span>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_320px]">
          <div className="flex flex-col gap-5">
            <div>
              <h1 className="text-2xl font-extrabold leading-tight tracking-tight text-foreground-950 md:text-3xl">
                {video.title}
              </h1>
              <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-foreground-500 md:text-sm">
                <span className="flex items-center gap-1 font-semibold text-accent-600">
                  <i className="ri-star-fill" />
                  {video.rating.toFixed(1)}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-fire-line text-accent-500" />
                  {formatPlays(video.plays)}次播放
                </span>
                <span>{video.year}</span>
                <span className="text-foreground-300">·</span>
                <span>{video.uploadDate} 上线</span>
              </div>
            </div>

            <section className="rounded-lg border border-background-200/80 bg-background-50 p-5">
              <h2 className="mb-3 flex items-center gap-2 text-base font-bold text-foreground-950">
                <i className="ri-file-text-line text-primary-600" />
                内容简介
              </h2>
              <p className="text-sm leading-relaxed text-foreground-600 md:text-base">
                {video.description}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {video.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-800"
                  >
                    # {tag}
                  </span>
                ))}
              </div>
            </section>

            <div className="flex items-start gap-3 rounded-lg border border-accent-200 bg-accent-50 p-4 text-sm text-accent-900">
              <i className="ri-information-line mt-0.5 text-lg text-accent-600" />
              <p>
                本站视频详情页以静态封面展示内容信息，在线播放功能为后续接入能力，当前不提供真实播放。
              </p>
            </div>
          </div>

          <aside className="flex flex-col gap-4">
            <button
              type="button"
              onClick={handleFavorite}
              className={`flex items-center justify-center gap-2 whitespace-nowrap rounded-lg px-6 py-3 text-sm font-semibold transition-colors ${
                favorited
                  ? 'bg-accent-500 text-background-50 hover:bg-accent-600'
                  : 'border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600'
              }`}
            >
              <i className={favorited ? 'ri-bookmark-fill text-lg' : 'ri-bookmark-line text-lg'} />
              {favorited ? '已收藏' : '收藏本片'}
            </button>

            <div className="rounded-lg border border-background-200/80 bg-background-50 p-5">
              <h2 className="mb-4 text-sm font-bold text-foreground-950">视频信息</h2>
              <dl className="flex flex-col gap-3">
                {infoRows.map((row) => (
                  <div key={row.label} className="flex items-center justify-between gap-3 text-sm">
                    <dt className="flex items-center gap-2 text-foreground-500">
                      <i className={`${row.icon} text-primary-500`} />
                      {row.label}
                    </dt>
                    <dd className="font-medium text-foreground-800">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <Link
              to={`/category/${category.key}`}
              className="flex items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-arrow-left-line" />
              返回{category.name}分类
            </Link>
          </aside>
        </div>
      </article>

      <RelatedVideos video={video} />

      {tip && (
        <div className="fixed bottom-24 left-1/2 z-50 -translate-x-1/2 animate-fade-up rounded-full bg-foreground-950/90 px-5 py-2.5 text-sm font-medium text-background-50 shadow-lg md:bottom-8">
          {tip}
        </div>
      )}
    </>
  );
}